/**
 * Section-aware living bar view model (experiment/section-scroll-bar).
 *
 * - Above bar: current section questions
 * - In bar: "Next section will answer…" + next pills + CTA "Get the answers now"
 *
 * Session-asked filtering reuses page-pills helpers.
 */

import {
  filterUnaskedPageQuestions,
  pickDiversePageQuestions,
} from "./page-pills.ts";
import { readConversationFocus } from "./conversation-focus.ts";

/** Max pills above the bar (current section). */
export const SECTION_CURRENT_PILLS = 2;
/** Max next-section pills inside the bar. */
export const SECTION_NEXT_PILLS = 2;

export const SECTION_CTA_LABEL = "Get the answers now";

/** Short, action-forward — invites a pill tap instead of more scrolling. */
export const SECTION_NEXT_LEAD = "Coming up next — tap to ask:";

export const SECTION_LAST_LEAD = "Almost done — tap a question or keep exploring.";

export type PageSection = {
  key: string;
  heading: string;
  questions: string[];
};

/** Resolved chrome for one scroll position. */
export type SectionBarView = {
  /** True when dual chrome should render (have ≥1 section with unasked Qs). */
  active: boolean;
  sectionIndex: number;
  currentHeading: string;
  currentQuestions: string[];
  nextHeading: string | null;
  nextQuestions: string[];
  /**
   * Full unasked inventory for the coming-up section (not just the 2 rail cards).
   * Used after a sidebar Answer-now when footer chrome is dead: panel shows up
   * to 3 of these — never invents, never flat page pool first.
   */
  comingUpPool: string[];
  /** In-bar lead copy. */
  lead: string;
  ctaLabel: string;
  /**
   * Panel open via bar CTA ("Get the answers now") without auto-send:
   * this-section + coming-up display set (discoverability).
   */
  openWithQuestions: string[];
};

export function normalizeHeadingKey(heading: string): string {
  return heading
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9$\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Parse sections from page-voice JSON (defensive). */
export function parsePageSections(raw: unknown): PageSection[] {
  if (!Array.isArray(raw)) return [];
  const out: PageSection[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    const heading = typeof o.heading === "string" ? o.heading.trim() : "";
    const keyRaw = typeof o.key === "string" ? o.key.trim() : "";
    const qs = Array.isArray(o.questions)
      ? o.questions.filter((q): q is string => typeof q === "string").map((q) => q.replace(/\s+/g, " ").trim()).filter(Boolean)
      : [];
    if (!heading || qs.length === 0) continue;
    out.push({
      key: keyRaw || normalizeHeadingKey(heading) || `s-${out.length}`,
      heading,
      questions: qs.slice(0, 6),
    });
  }
  return out;
}

/** Side-rail heading when conversation-ranked pills continue an engaged session. */
export const ENGAGED_RAIL_HEADING = "Next for you";
/** Coming-up heading under engaged ranked pills (extra beyond the main 3). */
export const ENGAGED_RAIL_NEXT_HEADING = "Also on this page";
export const ENGAGED_RAIL_LEAD = "Continuing from your conversation — tap to ask:";
/**
 * Engaged rail shows up to 3 ranked next-asks (same as panel MAX_PAGE_PILLS).
 * Do NOT use SECTION_CURRENT_PILLS (2) here — that caused the 3→2 shrink regression.
 */
export const ENGAGED_RAIL_PILLS = 3;

export type BuildSectionBarViewOpts = {
  /**
   * Server / context_next_ask ranked inventory. Only used when the visitor has
   * conversation focus (real engage). Cold pageview rank must NOT replace
   * section dual-chrome (that shrank site pills to 2).
   */
  engagedRanked?: readonly string[];
  /**
   * When true, allow engagedRanked overlay. Caller should set from
   * conversation-focus lastUser (chat this session). Default: only if focus
   * already has lastUser in sessionStorage.
   */
  forceEngaged?: boolean;
};

/**
 * Build the dual-chrome view for section index `i` (session-filtered).
 * Engaged overlay only when ranked list + conversation focus (not cold rank).
 */
export function buildSectionBarView(
  sections: readonly PageSection[],
  sectionIndex: number,
  opts?: BuildSectionBarViewOpts,
): SectionBarView {
  const inactive = (): SectionBarView => ({
    active: false,
    sectionIndex: 0,
    currentHeading: "",
    currentQuestions: [],
    nextHeading: null,
    nextQuestions: [],
    comingUpPool: [],
    lead: "",
    ctaLabel: SECTION_CTA_LABEL,
    openWithQuestions: [],
  });

  const focus = readConversationFocus();
  const lastUser = focus?.lastUser ?? null;
  const engaged = (opts?.engagedRanked ?? [])
    .map((q) => q.replace(/\s+/g, " ").trim())
    .filter((q) => q.length >= 6);
  const engagedUnasked = filterUnaskedPageQuestions(engaged, 8);
  // Gate: must have chat focus (or explicit force). Cold server-bar rank alone
  // must keep full section inventory (telemetry: 3→2 shrink on cold rank).
  const useEngagedOverlay =
    engagedUnasked.length > 0 &&
    (!!lastUser || opts?.forceEngaged === true);

  if (useEngagedOverlay) {
    // Main rail: full 3 ranked pills (parity with panel Suggested).
    const currentQs = engagedUnasked.slice(0, ENGAGED_RAIL_PILLS);
    const nextQs = engagedUnasked.slice(
      ENGAGED_RAIL_PILLS,
      ENGAGED_RAIL_PILLS + SECTION_NEXT_PILLS,
    );
    const comingUpPool = engagedUnasked.slice(0, 6);
    const openWithQuestions = filterUnaskedPageQuestions(
      engagedUnasked,
      ENGAGED_RAIL_PILLS,
    );
    return {
      active: true,
      sectionIndex: Math.max(0, Math.min(sectionIndex, Math.max(0, sections.length - 1))),
      currentHeading: ENGAGED_RAIL_HEADING,
      currentQuestions: currentQs,
      nextHeading: nextQs.length > 0 ? ENGAGED_RAIL_NEXT_HEADING : null,
      nextQuestions: nextQs,
      comingUpPool,
      lead: ENGAGED_RAIL_LEAD,
      ctaLabel: SECTION_CTA_LABEL,
      openWithQuestions,
    };
  }

  if (!sections.length) return inactive();

  // Prefer a section that still has unasked Qs at/after index; else last with any.
  let i = Math.max(0, Math.min(sectionIndex, sections.length - 1));
  const unaskedAt = (idx: number) =>
    filterUnaskedPageQuestions(sections[idx]?.questions ?? [], 6);

  // Walk forward if current is exhausted but later sections still have Qs.
  while (i < sections.length - 1 && unaskedAt(i).length === 0) i++;

  // Section fidelity first: only this / next section inventory.
  // Diversity collapses embedding twins — never invents outside the list.
  // Do NOT focus-rank the coming-up pool toward the last turn (that re-asks
  // the same topic after a rail click).
  const currentUnasked = unaskedAt(i);
  const currentQs = pickDiversePageQuestions(
    currentUnasked,
    SECTION_CURRENT_PILLS,
    { againstLastUser: lastUser },
  );
  let nextHeading: string | null = null;
  let nextUnasked: string[] = [];
  let nextQs: string[] = [];
  for (let j = i + 1; j < sections.length; j++) {
    const u = unaskedAt(j);
    if (u.length > 0) {
      nextHeading = sections[j]!.heading;
      nextUnasked = u;
      nextQs = pickDiversePageQuestions(u, SECTION_NEXT_PILLS, {
        againstLastUser: lastUser,
      });
      break;
    }
  }

  const hasAny = currentQs.length > 0 || nextQs.length > 0;
  if (!hasAny) return inactive();

  const lead =
    nextQs.length > 0
      ? SECTION_NEXT_LEAD
      : SECTION_LAST_LEAD;

  // Coming-up pool: full unasked next-section embeddings (for dead-chrome panel
  // after sidebar Answer-now). Cap at 6; diversity only — no focus re-rank.
  const comingUpPool = pickDiversePageQuestions(nextUnasked, 6, {
    againstLastUser: lastUser,
  });

  // Bar CTA open (no auto-send): show what the rail shows (this + coming-up cards).
  const openWithQuestions = filterUnaskedPageQuestions(
    [...currentQs, ...nextQs],
    SECTION_CURRENT_PILLS + SECTION_NEXT_PILLS,
  );

  return {
    active: true,
    sectionIndex: i,
    currentHeading: sections[i]?.heading ?? "",
    currentQuestions: currentQs,
    nextHeading,
    nextQuestions: nextQs,
    comingUpPool,
    lead,
    ctaLabel: SECTION_CTA_LABEL,
    openWithQuestions,
  };
}

/**
 * Map scroll progress [0,1] → section index (fallback when DOM headings miss).
 */
export function sectionIndexFromProgress(
  progress: number,
  sectionCount: number,
): number {
  if (sectionCount <= 1) return 0;
  const p = Math.max(0, Math.min(1, progress));
  // Bias slightly so top of page stays section 0 longer.
  const idx = Math.floor(p * sectionCount);
  return Math.max(0, Math.min(sectionCount - 1, idx));
}

/**
 * Content hints: live H2s are often marketing sentences while page-voice
 * sections use short labels ("THE DASHBOARD"). Map distinctive body phrases
 * → section key so scroll tracking works without site building blocks.
 */
const SECTION_CONTENT_HINTS: ReadonlyArray<{ section: string; phrases: string[] }> = [
  {
    section: "what you will see",
    phrases: [
      "seven answers",
      "website owner wishes",
      "story behind the numbers",
      "what visitors wanted what slowed",
    ],
  },
  {
    section: "the dashboard",
    phrases: ["dashboard look", "what does your dashboard", "in one clear view"],
  },
  {
    section: "why conversions happen",
    phrases: [
      "why visitors convert",
      "made visitors act",
      "made them leave",
      "see what made visitors",
    ],
  },
  {
    section: "vs traditional analytics",
    phrases: [
      "analytics shows what happened",
      "tells you why",
      "traditional analytics",
      "ga4 heatmaps",
    ],
  },
  {
    section: "the conversion loop",
    phrases: [
      "every visitor interaction",
      "better website",
      "conversion loop",
    ],
  },
  {
    section: "accuracy by design",
    phrases: [
      "zero hallucination",
      "unsupported claims",
      "accuracy by design",
    ],
  },
  {
    section: "seen enough",
    phrases: ["stop reading", "start asking", "seen enough"],
  },
  {
    section: "what visitors wanted",
    phrases: ["what visitors wanted", "questions and topics people cared"],
  },
  {
    section: "what slowed them down",
    phrases: ["what slowed them down", "friction that kept people"],
  },
  {
    section: "what helped them convert",
    phrases: ["what helped them convert", "moved people closer to action"],
  },
  {
    section: "what it always does",
    phrases: ["what it always does"],
  },
  {
    section: "what it never does",
    phrases: ["what it never does"],
  },
];

const MATCH_STOP = new Set([
  "the",
  "a",
  "an",
  "and",
  "or",
  "of",
  "to",
  "for",
  "in",
  "on",
  "your",
  "you",
  "what",
  "how",
  "does",
  "will",
  "with",
  "from",
  "this",
  "that",
  "into",
  "about",
  "why",
  "they",
  "them",
  "their",
  "are",
  "is",
  "was",
  "were",
  "been",
  "have",
  "has",
  "had",
  "can",
  "could",
  "should",
  "would",
  "every",
  "more",
  "than",
  "just",
  "only",
  "also",
  "not",
  "but",
  "our",
  "its",
  "who",
  "which",
  "when",
  "where",
  "bluehat",
  "bluehatai",
  "blue",
  "hat",
  "ai",
]);

function significantTokens(s: string): Set<string> {
  return new Set(
    s
      .split(/\s+/)
      .map((t) => t.trim())
      .filter((t) => t.length > 2 && !MATCH_STOP.has(t)),
  );
}

/**
 * Score how well live DOM text matches a page-voice section (0–100+).
 * Exact / contains / content-hint / token overlap.
 */
export function scoreSectionMatch(liveText: string, sectionHeading: string): number {
  const vk = normalizeHeadingKey(liveText);
  const hk = normalizeHeadingKey(sectionHeading);
  if (!vk || !hk || vk.length < 3) return 0;
  if (vk === hk) return 100;

  let score = 0;

  // Substring (comparable lengths only — avoids long H2 freezing on short labels).
  const shorter = Math.min(hk.length, vk.length);
  const longer = Math.max(hk.length, vk.length);
  if (shorter >= 8 && shorter / longer >= 0.45) {
    if (hk.includes(vk) || vk.includes(hk)) score = Math.max(score, 72 + shorter * 0.2);
  }

  // Content hints (marketing H2 → section label).
  for (const row of SECTION_CONTENT_HINTS) {
    if (row.section !== hk && !hk.includes(row.section) && !row.section.includes(hk)) {
      continue;
    }
    for (const phrase of row.phrases) {
      if (vk.includes(phrase)) score = Math.max(score, 88);
    }
  }
  // Also: if section key matches a hint row via live text alone.
  for (const row of SECTION_CONTENT_HINTS) {
    for (const phrase of row.phrases) {
      if (vk.includes(phrase) && (hk === row.section || hk.includes(row.section))) {
        score = Math.max(score, 90);
      }
    }
  }

  // Token overlap (dashboard ↔ "what does your dashboard look like").
  // Require ≥2 shared significant tokens OR a highly distinctive 1-token
  // hit (dashboard, analytics, …) so "website" alone can't freeze scroll.
  const DISTINCTIVE = new Set([
    "dashboard",
    "analytics",
    "hallucination",
    "accuracy",
    "pricing",
    "loop",
    "friction",
    "objections",
    "conversions",
    "conversion",
  ]);
  const ta = significantTokens(vk);
  const tb = significantTokens(hk);
  if (ta.size && tb.size) {
    let inter = 0;
    const shared: string[] = [];
    for (const t of ta) {
      if (tb.has(t)) {
        inter++;
        shared.push(t);
      }
    }
    if (inter > 0) {
      const jacc = inter / new Set([...ta, ...tb]).size;
      const cover = inter / Math.min(ta.size, tb.size);
      const distinctive = shared.some((t) => DISTINCTIVE.has(t));
      if (inter >= 2 || distinctive) {
        score = Math.max(score, cover * 55 + jacc * 30 + inter * 8 + (distinctive ? 20 : 0));
      }
    }
  }

  return score;
}

/** Minimum score to accept a live-text → section match. */
export const SECTION_MATCH_MIN = 42;

/**
 * Match a live DOM heading key to a page-voice section.
 * Prefers exact label match, then content-hints / token overlap for real H2s.
 */
export function matchSectionIndex(
  sections: readonly PageSection[],
  headingKey: string,
): number | null {
  if (!sections.length || !headingKey || headingKey.length < 3) return null;
  const vk = normalizeHeadingKey(headingKey);

  // Exact heading / key match first.
  for (let i = 0; i < sections.length; i++) {
    const hk = normalizeHeadingKey(sections[i]!.heading);
    if (hk && hk === vk) return i;
    const sk = sections[i]!.key.replace(/__\d+$/, "");
    if (sk && sk === vk) return i;
  }

  let best: number | null = null;
  let bestScore = 0;
  for (let i = 0; i < sections.length; i++) {
    const sc = scoreSectionMatch(vk, sections[i]!.heading);
    if (sc > bestScore) {
      bestScore = sc;
      best = i;
    }
  }
  if (best != null && bestScore >= SECTION_MATCH_MIN) return best;
  return null;
}

/**
 * Best section index from live headings above the reading line.
 * `activeKeys` ordered top→bottom; the *last* matching key wins (furthest
 * into the page among headings already passed).
 */
export function sectionIndexFromVisibleHeadings(
  sections: readonly PageSection[],
  activeKeys: readonly string[],
): number | null {
  if (!sections.length || !activeKeys.length) return null;
  let best: number | null = null;
  for (const vk of activeKeys) {
    const idx = matchSectionIndex(sections, vk);
    if (idx != null) best = idx;
  }
  return best;
}

/**
 * Viewport-aware section pick from live anchors (key + viewport top).
 *
 * 1. Among anchors at/above the focus line → furthest down the page (current
 *    section the visitor has entered).
 * 2. Else nearest upcoming anchor below the focus line.
 * 3. null if nothing matched a page-voice section.
 *
 * Using positions (not only key lists) keeps the stage moving when later
 * eyebrow pills like "THE DASHBOARD" enter the reading zone.
 */
export function sectionIndexFromAnchors(
  sections: readonly PageSection[],
  anchors: ReadonlyArray<{ key: string; top: number }>,
  focusY: number,
): number | null {
  if (!sections.length || !anchors.length) return null;

  const hits: { idx: number; top: number }[] = [];
  for (const a of anchors) {
    const idx = matchSectionIndex(sections, a.key);
    if (idx == null) continue;
    hits.push({ idx, top: a.top });
  }
  if (!hits.length) return null;

  // Entered sections: anchor top at or above focus (with small slack so a
  // heading just below the line still counts while reading its body).
  const slack = 48;
  const entered = hits.filter((h) => h.top <= focusY + slack);
  if (entered.length > 0) {
    // Furthest into the page among entered = largest top (closest to focus).
    entered.sort((a, b) => a.top - b.top);
    return entered[entered.length - 1]!.idx;
  }

  // Still above first section label → first upcoming by document order.
  hits.sort((a, b) => a.top - b.top);
  return hits[0]!.idx;
}
